import { Mail, Phone, MapPin, Clock, Factory, Instagram, Facebook } from "lucide-react";
import { useLang, CONTACT, whatsappLink } from "../i18n";
import { Reveal, Eyebrow } from "../components/Reveal";
import { WhatsAppIcon } from "../components/WhatsAppButton";

export default function Contact() {
  const { t } = useLang();

  const rows = [
    { icon: Factory, label: t.contact_factory_label, value: CONTACT.factory, href: "https://www.google.com/maps/search/?api=1&query=Via+Aurelia+Km+41,600+Cerveteri", testid: "contact-factory" },
    { icon: MapPin, label: t.contact_hq_label, value: CONTACT.hq, href: `https://www.google.com/maps/search/?api=1&query=${CONTACT.mapLat},${CONTACT.mapLng}`, testid: "contact-address" },
    { icon: Phone, label: t.contact_phone_label, value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/\s/g, "")}`, testid: "contact-phone" },
    { icon: Mail, label: t.contact_email_label, value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email" },
    { icon: Clock, label: t.contact_hours_label, value: t.contact_hours, testid: "contact-hours" },
  ];

  const d = 0.02;
  const bbox = `${CONTACT.mapLng - d * 1.6}%2C${CONTACT.mapLat - d}%2C${CONTACT.mapLng + d * 1.6}%2C${CONTACT.mapLat + d}`;

  return (
    <div data-testid="contact-page" className="pt-32 sm:pt-40 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>{t.contact_eyebrow}</Eyebrow>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] text-[#F3EFE6]">{t.contact_title}</h1>
          <p className="mt-6 max-w-xl text-sm sm:text-base font-light text-[#A39E93] leading-relaxed">{t.contact_sub}</p>
          <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-cta" className="mt-8 inline-flex items-center gap-3 bg-[#C8A86B] px-7 py-3.5 text-sm uppercase tracking-[0.15em] text-[#080808] transition-colors duration-300 hover:bg-[#D8BD85]">
            <WhatsAppIcon size={16} /> {t.contact_whatsapp_cta}
          </a>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            {rows.map((r, i) => (
              <Reveal key={r.label} delay={i * 0.06}>
                <div className="flex gap-5 border-t border-[#1C1A17] py-7">
                  <r.icon size={18} className="mt-1 shrink-0 text-[#C8A86B]" />
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#827D72]">{r.label}</p>
                    {r.href ? (
                      <a data-testid={r.testid} href={r.href} target={r.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="mt-2 block text-base font-light text-[#F3EFE6] transition-colors hover:text-[#D8BD85]">{r.value}</a>
                    ) : (
                      <p data-testid={r.testid} className="mt-2 text-base font-light text-[#F3EFE6] leading-relaxed">{r.value}</p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="flex items-center gap-5 border-t border-[#1C1A17] py-7">
                <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#827D72]">{t.contact_social_label}</p>
                <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram" aria-label="Instagram" className="text-[#A39E93] transition-colors hover:text-[#C8A86B]"><Instagram size={18} /></a>
                <a href={CONTACT.facebook} target="_blank" rel="noopener noreferrer" data-testid="contact-facebook" aria-label="Facebook" className="text-[#A39E93] transition-colors hover:text-[#C8A86B]"><Facebook size={18} /></a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="h-full min-h-[420px] overflow-hidden border border-[#262420]">
              <iframe
                data-testid="contact-map"
                title="Arte & Infissi map"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${CONTACT.mapLat}%2C${CONTACT.mapLng}`}
                className="map-dark h-full min-h-[420px] w-full border-0"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
